<template>
  <div class="skd-root">

    <!-- ══════════════════════════════
         COL 1 — Assigned To Me
    ══════════════════════════════ -->
    <div class="skd-col skd-col--assigned">

      <!-- panel header -->
      <div class="skd-panel-header">
        <div class="skd-s skd-s--circle-sm"></div>
        <div class="skd-s skd-s--label-lg"></div>
        <div class="skd-s skd-s--pill-xs skd-ml-auto"></div>
      </div>

      <!-- filter / search bar -->
      <div class="skd-search-bar">
        <div class="skd-s skd-s--search"></div>
        <div class="skd-s skd-s--btn-icon"></div>
      </div>

      <!-- issue rows -->
      <div class="skd-issue-list">
        <div
          class="skd-issue-card"
          v-for="n in 7"
          :key="n"
          :style="{ animationDelay: n * 55 + 'ms' }"
        >
          <div class="skd-s skd-s--avatar"></div>
          <div class="skd-issue-body">
            <div class="skd-issue-row">
              <div class="skd-s skd-s--pill-xs"></div>
              <div class="skd-s skd-s--pill-xs"></div>
              <div class="skd-s skd-s--pill-xs skd-ml-auto"></div>
            </div>
            <div class="skd-s skd-s--line skd-s--w95 skd-mt5"></div>
            <div class="skd-s skd-s--line skd-s--w65 skd-mt4"></div>
            <div class="skd-issue-row skd-mt5">
              <div class="skd-s skd-s--avatar-xs"></div>
              <div class="skd-s skd-s--pill-xs"></div>
              <div class="skd-s skd-s--pill-xs"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════
         COL 2 — Timezone Clock + Timesheet
    ══════════════════════════════ -->
    <div class="skd-col skd-col--center">

      <!-- Timezone clock skeleton -->
      <div class="skd-tz-card">
        <!-- header row -->
        <div class="skd-tz-header">
          <div class="skd-s skd-s--circle-xs"></div>
          <div class="skd-s skd-s--label-sm"></div>
          <div class="skd-s skd-s--live-dot skd-ml-auto"></div>
          <div class="skd-s skd-s--toggle"></div>
        </div>
        <!-- three analog clock circles -->
        <div class="skd-tz-clocks">
          <div class="skd-tz-clock-item" v-for="n in 3" :key="'tz'+n" :style="{ animationDelay: n * 80 + 'ms' }">
            <div class="skd-s skd-s--analog-face"></div>
            <div class="skd-s skd-s--label-sm skd-mt6" style="width:36px;"></div>
            <div class="skd-s skd-s--label-sm skd-mt3" style="width:52px;"></div>
          </div>
        </div>
      </div>

      <!-- Timesheet card -->
      <div class="skd-card skd-ts-card">
        <!-- header -->
        <div class="skd-panel-header">
          <div class="skd-s skd-s--circle-sm"></div>
          <div class="skd-s skd-s--label-lg"></div>
          <div class="skd-s skd-s--btn-icon skd-ml-auto"></div>
          <div class="skd-s skd-s--btn-icon"></div>
        </div>
        <!-- date tabs -->
        <div class="skd-tab-row">
          <div class="skd-s skd-s--tab-active"></div>
          <div class="skd-s skd-s--tab"></div>
          <div class="skd-s skd-s--tab"></div>
        </div>
        <!-- table head -->
        <div class="skd-s skd-s--thead"></div>
        <!-- group + rows  ×2 -->
        <template v-for="g in 2" :key="'g'+g">
          <div class="skd-s skd-s--group" :class="g > 1 ? 'skd-mt12' : 'skd-mt8'"></div>
          <div
            class="skd-ts-row"
            v-for="r in (g === 1 ? 4 : 3)"
            :key="'r'+g+r"
          >
            <div class="skd-s skd-s--ts-chip"></div>
            <div class="skd-s skd-s--ts-time"></div>
            <div class="skd-s skd-s--ts-ref"></div>
            <div class="skd-s skd-s--ts-cat"></div>
            <div class="skd-s skd-s--ts-dur"></div>
          </div>
        </template>
      </div>
    </div>

    <!-- ══════════════════════════════
         COL 3 — Stat Cards + Insights
    ══════════════════════════════ -->
    <div class="skd-col skd-col--right">

      <!-- 2×2 stat cards -->
      <div class="skd-stats-grid">
        <div
          class="skd-stat-card"
          v-for="n in 4"
          :key="'s'+n"
          :style="{ animationDelay: n * 60 + 'ms' }"
        >
          <div class="skd-s skd-s--stat-icon"></div>
          <div class="skd-stat-body">
            <div class="skd-s skd-s--line skd-s--w55"></div>
            <div class="skd-s skd-s--value skd-mt6"></div>
          </div>
          <div class="skd-s skd-s--stat-bubble"></div>
        </div>
      </div>

      <!-- ErrorTrends card -->
      <div class="skd-card skd-insight-card">
        <div class="skd-panel-header">
          <div class="skd-s skd-s--circle-sm"></div>
          <div class="skd-s skd-s--label-lg"></div>
          <div class="skd-s skd-s--pill-xs skd-ml-auto"></div>
        </div>
        <div class="skd-s skd-s--chart-bar"></div>
        <div class="skd-chart-legend">
          <template v-for="n in 3" :key="'l'+n">
            <div class="skd-s skd-s--legend-dot"></div>
            <div class="skd-s skd-s--legend-label"></div>
          </template>
        </div>
      </div>

      <!-- DoYouKnow card -->
      <div class="skd-card skd-insight-card">
        <div class="skd-panel-header">
          <div class="skd-s skd-s--circle-sm"></div>
          <div class="skd-s skd-s--label-lg"></div>
        </div>
        <div class="skd-s skd-s--spotlight"></div>
        <div class="skd-dyk-rows">
          <div class="skd-dyk-row" v-for="n in 4" :key="'d'+n">
            <div class="skd-s skd-s--dyk-num"></div>
            <div class="skd-s skd-s--dyk-line" :style="{ width: [90, 70, 82, 60][n-1] + '%' }"></div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
export default { name: "SkeletonForDashboard" };
</script>

<style scoped>
/* ══════════════════════════════════════
   ROOT — mirrors dash-desktop 3-col
══════════════════════════════════════ */
.skd-root {
  display: flex;
  gap: 10px;
  height: calc(100vh - 88px);
  padding: 12px 10px;
  background: #f1f5f9;
  box-sizing: border-box;
  overflow: hidden;
}

/* ── COLUMNS ── */
.skd-col {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  overflow: hidden;
}
.skd-col--assigned { flex: 3;   min-width: 280px; background: #fff; border-radius: 14px; border: 1px solid #e9edf2; box-shadow: 0 2px 8px rgba(0,0,0,0.05); padding: 14px 12px 12px; }
.skd-col--center   { flex: 3.5; min-width: 320px; }
.skd-col--right    { flex: 3;   min-width: 0; }

/* ══════════════════════════════════════
   SHIMMER BASE
══════════════════════════════════════ */
.skd-s {
  border-radius: 6px;
  background: linear-gradient(90deg, #eaeff8 25%, #dde4f0 50%, #eaeff8 75%);
  background-size: 300% 100%;
  animation: skd-sweep 1.7s ease-in-out infinite;
  flex-shrink: 0;
}
@keyframes skd-sweep {
  0%   { background-position: 200% 0; }
  100% { background-position: -100% 0; }
}

/* ── SHARED CARD SHELL ── */
.skd-card {
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e9edf2;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  padding: 14px 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow: hidden;
}

/* ══════════════════════════════════════
   ATOMS
══════════════════════════════════════ */
.skd-ml-auto         { margin-left: auto; }
.skd-mt3             { margin-top: 3px; }
.skd-mt4             { margin-top: 4px; }
.skd-mt5             { margin-top: 5px; }
.skd-mt6             { margin-top: 6px; }
.skd-mt8             { margin-top: 8px; }
.skd-mt12            { margin-top: 12px; }

.skd-s--circle-xs    { width: 18px;  height: 18px; border-radius: 50%; }
.skd-s--circle-sm    { width: 28px;  height: 28px; border-radius: 50%; }
.skd-s--avatar       { width: 38px;  height: 38px; border-radius: 50%; flex-shrink: 0; }
.skd-s--avatar-xs    { width: 18px;  height: 18px; border-radius: 50%; flex-shrink: 0; }
.skd-s--label-lg     { width: 110px; height: 12px; }
.skd-s--label-sm     { width: 72px;  height: 10px; }
.skd-s--pill-xs      { width: 42px;  height: 18px; border-radius: 20px; }
.skd-s--live-dot     { width: 8px;   height: 8px;  border-radius: 50%; }
.skd-s--toggle       { width: 54px;  height: 26px; border-radius: 8px; }
.skd-s--search       { flex: 1;      height: 30px; border-radius: 8px; }
.skd-s--btn-icon     { width: 28px;  height: 28px; border-radius: 8px; }
.skd-s--line         { height: 10px; border-radius: 5px; }
.skd-s--w95          { width: 95%; }
.skd-s--w65          { width: 65%; }
.skd-s--w55          { width: 55%; }
.skd-s--analog-face  { width: 100%; aspect-ratio: 1/1; border-radius: 50%; }
.skd-s--tab          { width: 72px;  height: 26px; border-radius: 20px; }
.skd-s--tab-active   { width: 72px;  height: 26px; border-radius: 20px;
  background: linear-gradient(90deg, #dce6f5 25%, #cdd9f0 50%, #dce6f5 75%);
  background-size: 300% 100%;
  animation: skd-sweep 1.7s ease-in-out infinite;
}
.skd-s--thead        { height: 30px; width: 100%; border-radius: 8px; }
.skd-s--group        { height: 10px; width: 28%; }
.skd-s--ts-chip      { width: 60px;  height: 20px; border-radius: 6px; }
.skd-s--ts-time      { width: 100px; height: 10px; }
.skd-s--ts-ref       { width: 68px;  height: 10px; }
.skd-s--ts-cat       { flex: 1;      height: 10px; }
.skd-s--ts-dur       { width: 40px;  height: 10px; }
.skd-s--stat-icon    { width: 40px;  height: 40px; border-radius: 10px; flex-shrink: 0; }
.skd-s--stat-bubble  { width: 52px;  height: 52px; border-radius: 50%; position: absolute; bottom: -12px; right: -12px; opacity: 0.25; }
.skd-s--value        { height: 20px; width: 55%; border-radius: 5px; }
.skd-s--chart-bar    { height: 88px; width: 100%; border-radius: 10px; }
.skd-s--spotlight    { height: 80px; width: 100%; border-radius: 12px; }
.skd-s--legend-dot   { width: 10px;  height: 10px; border-radius: 50%; }
.skd-s--legend-label { width: 50px;  height: 9px; margin-left: 4px; }
.skd-s--dyk-num      { width: 16px;  height: 10px; flex-shrink: 0; }
.skd-s--dyk-line     { height: 10px; border-radius: 5px; }

/* ══════════════════════════════════════
   COL 1 — ASSIGNED
══════════════════════════════════════ */
.skd-panel-header    { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.skd-search-bar      { display: flex; align-items: center; gap: 6px; }

.skd-issue-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow: hidden;
  flex: 1;
}
.skd-issue-card {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 10px 8px;
  border-radius: 10px;
  background: #f8fafc;
  flex-shrink: 0;
}
.skd-issue-body  { display: flex; flex-direction: column; flex: 1; min-width: 0; }
.skd-issue-row   { display: flex; align-items: center; gap: 6px; }

/* ══════════════════════════════════════
   COL 2 — TIMEZONE CLOCK SKELETON
══════════════════════════════════════ */
.skd-tz-card {
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e9edf2;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex-shrink: 0;
}
.skd-tz-header {
  display: flex;
  align-items: center;
  gap: 7px;
}
.skd-tz-clocks {
  display: flex;
  gap: 10px;
  justify-content: space-between;
}
.skd-tz-clock-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* ══════════════════════════════════════
   COL 2 — TIMESHEET
══════════════════════════════════════ */
.skd-ts-card {
  flex: 1;
  min-height: 0;
}
.skd-tab-row {
  display: flex;
  gap: 6px;
}
.skd-ts-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 7px 0;
  border-bottom: 1px solid #f1f5f9;
}

/* ══════════════════════════════════════
   COL 3 — STAT CARDS
══════════════════════════════════════ */
.skd-stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  flex-shrink: 0;
}
.skd-stat-card {
  background: #fff;
  border-radius: 14px;
  padding: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #f1f5f9;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  position: relative;
  overflow: hidden;
}
.skd-stat-body { display: flex; flex-direction: column; gap: 0; flex: 1; min-width: 0; }

/* ══════════════════════════════════════
   COL 3 — INSIGHT CARDS
══════════════════════════════════════ */
.skd-insight-card {
  flex: 1;
  min-height: 0;
}
.skd-chart-legend {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.skd-dyk-rows {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.skd-dyk-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
